import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  EventNote as LeaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import * as actions from '../../redux/actions';
import { showAlert } from '../../utils/functions/globalDialog';
import { clearAiPrefill, takeAiPrefill } from '../../utils/functions/aiPrefill';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

dayjs.locale('th');

const LEAVE_TYPE_LABELS = {
  SICK: 'ลาป่วย',
  PERSONAL: 'ลากิจ',
  VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด',
  ORDINATION: 'ลาบวช',
  UNPAID: 'ลาโดยไม่รับค่าจ้าง',
};

const PERIOD_LABELS = {
  FULL_DAY: 'เต็มวัน',
  MORNING: 'ครึ่งวันเช้า',
  AFTERNOON: 'ครึ่งวันบ่าย',
};

function calcTotalDays(startDate, endDate, period) {
  if (!startDate || !endDate) return 0;
  const s = dayjs(startDate);
  const e = dayjs(endDate);
  if (e.isBefore(s)) return 0;
  const diff = e.diff(s, 'day') + 1;
  if (period === 'FULL_DAY') return diff;
  return diff === 1 ? 0.5 : diff - 0.5;
}

export default function AddLeaveRequest({ title }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const me = useSelector((state) => state.me);
  const profile = me?.userData;

  const today = dayjs().format('YYYY-MM-DD');

  // ค่าที่ Coreplan AI กรอกให้ (อ่านครั้งเดียวตอนเปิดหน้า) — ผู้ใช้ตรวจ/แก้แล้วกดส่งเอง
  const [aiPrefill] = useState(() => takeAiPrefill('leave_request'));
  const [form, setForm] = useState({
    leave_type: '',
    start_date: today,
    end_date: today,
    period: 'FULL_DAY',
    reason: '',
    ...(aiPrefill || {}),
  });
  useEffect(() => { clearAiPrefill(); }, []);
  const [medCertFile, setMedCertFile] = useState(null);
  const [medCertPreview, setMedCertPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadedImageId, setUploadedImageId] = useState(null);

  const totalDays = calcTotalDays(form.start_date, form.end_date, form.period);
  const showMedCert = form.leave_type === 'SICK' && totalDays > 3;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMedCertFile(file);
    setMedCertPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.leave_type) errs.leave_type = 'กรุณาเลือกประเภทการลา';
    if (!form.start_date) errs.start_date = 'กรุณาเลือกวันที่เริ่มลา';
    if (!form.end_date) errs.end_date = 'กรุณาเลือกวันที่สิ้นสุดลา';
    if (form.end_date < form.start_date) errs.end_date = 'วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มลา';
    if (!form.reason.trim()) errs.reason = 'กรุณากรอกเหตุผลการลา';
    if (totalDays <= 0) errs.end_date = 'จำนวนวันลาต้องมากกว่า 0';
    return errs;
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const api = (await import('../../utils/functions/api')).default;
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/upload/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data?._id || data?.id || null;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      let imageId = uploadedImageId;
      if (showMedCert && medCertFile && !imageId) {
        imageId = await uploadImage(medCertFile);
        setUploadedImageId(imageId);
      }
      const payload = {
        employee: profile?._id,
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        period: form.period,
        total_days: totalDays,
        reason: form.reason,
        request_date: new Date().toISOString(),
        ...(showMedCert && imageId ? { medical_certificate: imageId } : {}),
      };
      await dispatch(actions.leaveRequestCreate(payload));
      showAlert('ยื่นคำขอลาเรียบร้อยแล้ว');
      history.push('/profile/leave');
    } catch (err) {
      showAlert('เกิดข้อผิดพลาด: ' + (err?.message || 'กรุณาลองใหม่'), { status: err?.response?.status });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <LeaveIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {title || 'ยื่นคำขอลา'}
        </Typography>
      </Box>

      {/* ข้อมูลพนักงาน (auto-fill) */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <CardContent>
          <Typography variant="subtitle2" color="primary.main" fontWeight={700} mb={1.5}>
            ข้อมูลพนักงาน
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">ชื่อ-นามสกุล</Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatEmployeeThaiName(profile)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">รหัสพนักงาน</Typography>
              <Typography variant="body1" fontWeight={600}>{profile?.employee_id || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">แผนก</Typography>
              <Typography variant="body1" fontWeight={600}>{profile?.department?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">ตำแหน่ง</Typography>
              <Typography variant="body1" fontWeight={600}>{profile?.role?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">สาขา / ทีม</Typography>
              <Typography variant="body1" fontWeight={600}>{profile?.team || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">วันที่ยื่นคำขอ</Typography>
              <Typography variant="body1" fontWeight={600}>
                {dayjs().format('DD/MM/YYYY')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {aiPrefill && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Coreplan AI กรอกฟอร์มให้แล้ว — กรุณาตรวจสอบ{aiPrefill.reason ? '' : ' และกรอกเหตุผลการลา'}ก่อนกดส่ง
        </Alert>
      )}

      {/* ฟอร์มคำขอลา */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>
            รายละเอียดการลา
          </Typography>
          <Grid container spacing={3}>
            {/* ประเภทการลา */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.leave_type}>
                <InputLabel>ประเภทการลา *</InputLabel>
                <Select
                  value={form.leave_type}
                  label="ประเภทการลา *"
                  onChange={handleChange('leave_type')}
                >
                  {Object.entries(LEAVE_TYPE_LABELS).map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
                {errors.leave_type && <FormHelperText>{errors.leave_type}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* ช่วงเวลา */}
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: 14 }}>ช่วงเวลา</FormLabel>
                <RadioGroup row value={form.period} onChange={handleChange('period')}>
                  {Object.entries(PERIOD_LABELS).map(([val, label]) => (
                    <FormControlLabel key={val} value={val} control={<Radio size="small" />} label={label} />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* วันที่เริ่มลา */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="วันที่เริ่มลา *"
                type="date"
                value={form.start_date}
                onChange={handleChange('start_date')}
                InputLabelProps={{ shrink: true }}
                error={!!errors.start_date}
                helperText={errors.start_date}
              />
            </Grid>

            {/* วันที่สิ้นสุดลา */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="วันที่สิ้นสุดลา *"
                type="date"
                value={form.end_date}
                onChange={handleChange('end_date')}
                inputProps={{ min: form.start_date }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.end_date}
                helperText={errors.end_date}
              />
            </Grid>

            {/* จำนวนวันลา */}
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">จำนวนวันลา</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {totalDays}
                </Typography>
                <Typography variant="caption" color="text.secondary">วัน</Typography>
              </Box>
            </Grid>

            {/* เหตุผลการลา */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="เหตุผลการลา *"
                value={form.reason}
                onChange={handleChange('reason')}
                error={!!errors.reason}
                helperText={errors.reason}
              />
            </Grid>

            {/* ใบรับรองแพทย์ (เฉพาะลาป่วย > 3 วัน) */}
            {showMedCert && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  การลาป่วยเกิน 3 วัน ต้องแนบใบรับรองแพทย์
                </Alert>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  แนบใบรับรองแพทย์
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                </Button>
                {medCertPreview && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      ไฟล์: {medCertFile?.name}
                    </Typography>
                    {medCertFile?.type?.startsWith('image/') && (
                      <Box mt={0.5}>
                        <img
                          src={medCertPreview}
                          alt="ใบรับรองแพทย์"
                          style={{ maxHeight: 160, borderRadius: 4, border: '1px solid #ddd' }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => history.push('/profile/leave')} disabled={submitting}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {submitting ? 'กำลังส่ง...' : 'ยื่นคำขอลา'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
